import { Reshaped, View } from '@zinc/ui';
import React, { type PropsWithChildren, useEffect } from 'react';
import '@zinc/ui/original-src/themes/reshaped/theme.css';
import '../styles/reshaped-legacy-hacks.css';
import { Theme } from '@zinc/ui';
import { baseThemeDefinition, generateThemeColors, getThemeCSS } from 'reshaped/themes';
import { cache } from 'tm3-core/cache';

export const NewUXContext = React.createContext(false);

const themes = {
  TM3: {
    color: {
      ...generateThemeColors({ primary: '#009ee0' }),
      foregroundNeutral: { hex: '#07093c' },
      foregroundNeutralFaded: { hex: '#5b667e' },
      foregroundPrimary: { hex: '#009ee0', hexDark: '#009ee0' },
      borderNeutralFaded: { hex: '#c7e1ed' },
      borderPrimary: { hex: '#009ee0', hexDark: '#8c8bf3' },
      backgroundPageFaded: { hex: '#f9f9fb' },
      backgroundNeutral: { hex: '#dfe2ea' },
      backgroundNeutralFaded: { hex: '#f7f8fa' },
      backgroundPrimary: { hex: '#009ee0', hexDark: '#009ee0' },
      backgroundPrimaryHighlighted: { hex: '#009ee0', hexDark: '#009ee0' },
      brand: { hex: '#07093c', hexDark: '#07093c' },
      foregroundCritical: { hex: '#c42525' }
    }
  },

  'E-Clinic': {
    color: {
      ...generateThemeColors({ primary: '#0067c3' }),
      foregroundNeutral: { hex: '#00427d' },
      foregroundNeutralFaded: { hex: '#5D8BB5' },
      foregroundPrimary: { hex: '#0067c3', hexDark: '#0067c3' },
      borderNeutralFaded: { hex: '#c7e1ed' },
      borderPrimary: { hex: '#0067c3', hexDark: '#0067c3' },
      backgroundPageFaded: { hex: '#f9f9fb' },
      backgroundNeutral: { hex: '#d0eaf6' },
      backgroundNeutralFaded: { hex: '#e9f5fb' },
      backgroundPrimary: { hex: '#0067c3', hexDark: '#0067c3' },
      backgroundPrimaryHighlighted: { hex: '#0067c3', hexDark: '#0067c3' },
      brand: { hex: '#0067c3', hexDark: '#0067c3' },
      foregroundCritical: { hex: '#c42525' }
    }
  },

  PPM: {
    color: {
      ...generateThemeColors({ primary: '#3593F2' }),
      foregroundNeutral: { hex: '#45515f' },
      foregroundNeutralFaded: { hex: '#697685' },
      foregroundPrimary: { hex: '#3c4550', hexDark: '#536273' },
      borderNeutralFaded: { hex: '#cbd0d5' },
      borderPrimary: { hex: '#3593F2', hexDark: '#3593F2' },
      backgroundPageFaded: { hex: '#f7f8f9' },
      backgroundNeutral: { hex: '#dde0e3' },
      backgroundNeutralFaded: { hex: '#f2f3f5' },
      backgroundPrimary: { hex: '#697b8e', hexDark: '#697b8e' },
      backgroundPrimaryHighlighted: { hex: '#3593F2', hexDark: '#3593F2' },
      brand: { hex: '#3c4550', hexDark: '#3c4550' },
      foregroundCritical: { hex: '#c42525' },
      sidebarBackgroundActive: { hex: '#cf382d', hexDark: '#cf382d' }
    }
  }
};

export default function NewUX({
  children,
  isConnect = false,
  primary = '#009EE0'
}: PropsWithChildren & { isConnect?: boolean; primary?: string }) {
  const { productName } = cache;

  useEffect(() => {
    const previousTheme = document.body.getAttribute('data-rs-theme');

    document.body.setAttribute('data-rs-theme', productName);

    return () => {
      if (previousTheme) {
        document.body.setAttribute('data-rs-theme', previousTheme);
        return;
      }

      document.body.removeAttribute('data-rs-theme');
    };
  }, [productName]);

  const overrides = { ...(themes[productName as keyof typeof themes] ?? themes.TM3) };
  const theme = Object.assign({}, baseThemeDefinition, overrides);
  const baseCss = isConnect
    ? getThemeCSS(
        productName,
        {
          ...baseThemeDefinition,
          color: generateThemeColors({
            primary: primary
          })
        },
        { colorContrastAlgorithm: 'apca' }
      )
    : getThemeCSS(productName, theme, {
        colorContrastAlgorithm: 'apca'
      });

  let printOverride = '';

  if (productName === 'E-Clinic') {
    printOverride = `
    @media print {
      [data-rs-theme="${productName}"] {
        --rs-color-foreground-neutral: #000000;
      }
    }
  `;
  }

  const css = baseCss + printOverride;

  return (
    <>
      {css && <style>{css}</style>}
      <Reshaped>
        <Theme name={productName}>
          <View backgroundColor="page" minHeight="100%">
            {children}
          </View>
        </Theme>
      </Reshaped>
    </>
  );
}
